import { useState } from "react";
import api from "@/lib/api";
import { CATEGORIES } from "@/lib/categories";
import { Mail, Check, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

export const CONSENT_TEXT =
  "Acconsento al trattamento del mio indirizzo email da parte di Collezionatico per ricevere la newsletter " +
  "settimanale, secondo l'informativa privacy. Posso revocare il consenso in qualsiasi momento.";

export function CategoryChips({ selected, onToggle, testidPrefix = "newsletter-cat" }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="newsletter-categories">
      {CATEGORIES.map((c) => {
        const on = selected.includes(c.slug);
        return (
          <button key={c.slug} type="button" onClick={() => onToggle(c.slug)} data-testid={`${testidPrefix}-${c.slug}`}
            style={{ "--cat": c.hex }}
            className={`nav-pill nav-pill-cat ${on ? "is-active" : ""}`}>
            <span className="dot" />{c.short}
          </button>
        );
      })}
    </div>
  );
}

export function NewsletterSignup() {
  const [email, setEmail] = useState("");
  const [cats, setCats] = useState([]);
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [msg, setMsg] = useState("");

  const toggle = (slug) =>
    setCats((prev) => (prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]));

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    if (!consent) {
      setStatus("error");
      setMsg("Per iscriverti devi acconsentire al trattamento dei dati.");
      return;
    }
    setStatus("loading"); setMsg("");
    try {
      const res = await api.post("/subscribe", {
        email: email.trim(), categories: cats, consent: true, consent_text: CONSENT_TEXT,
      });
      setStatus("done"); setMsg(res.data.message || "Iscrizione completata!");
      setEmail(""); setConsent(false);
    } catch (err) {
      setStatus("error");
      const d = err.response?.data?.detail;
      setMsg(typeof d === "string" ? d : "Inserisci un indirizzo email valido.");
    }
  };

  return (
    <div data-testid="newsletter-signup" className="rounded-3xl bg-panel border-2 border-line overflow-hidden sticker-soft">
      <div className="flex h-1.5 w-full" aria-hidden="true">
        {CATEGORIES.map((c) => <span key={c.slug} className="flex-1" style={{ background: c.hex }} />)}
      </div>
      <div className="p-6 sm:p-8">
      <div className="flex items-center gap-2 mb-2 text-gold">
        <Mail size={15} />
        <span className="font-meta text-[10px] uppercase tracking-[0.22em]">Newsletter settimanale</span>
      </div>
      <h3 className="font-editorial font-extrabold text-xl sm:text-2xl text-stone-900 tracking-tight">
        Il meglio della settimana, <span className="mark-hl">su misura</span>
      </h3>
      <p className="text-sm mt-2 mb-5 text-stone-600 max-w-xl">
        Ogni domenica notizie, valori indicativi e consigli pratici. Scegli le categorie che ti interessano.
      </p>

      {status === "done" ? (
        <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold" data-testid="newsletter-success">
          <Check size={16} /> {msg}
        </div>
      ) : (
        <form onSubmit={submit}>
          <CategoryChips selected={cats} onToggle={toggle} />
          <p className="text-[11px] mt-3 mb-4 text-stone-400">Nessuna categoria selezionata = ricevi tutte.</p>
          <div className="flex flex-col sm:flex-row gap-2.5 max-w-md">
            <input
              data-testid="newsletter-email-input"
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="La tua email"
              className="flex-1 px-4 py-3 rounded-full outline-none text-sm bg-ink border-2 border-line text-stone-900 placeholder:text-stone-400 focus:border-stone-900 transition-colors"
            />
            <button type="submit" disabled={status === "loading"} data-testid="newsletter-submit"
              className="px-6 py-3 rounded-full text-sm font-bold whitespace-nowrap bg-stone-900 text-white hover:-translate-y-0.5 transition-transform disabled:opacity-60 flex items-center justify-center sticker">
              {status === "loading" ? <Loader2 size={16} className="animate-spin" /> : "Iscriviti"}
            </button>
          </div>
          <label className="flex items-start gap-2.5 mt-4 max-w-xl cursor-pointer" data-testid="newsletter-consent-label">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
              data-testid="newsletter-consent" className="mt-0.5 w-4 h-4 accent-stone-900 shrink-0" />
            <span className="text-[11px] leading-relaxed text-stone-500">
              Acconsento al trattamento del mio indirizzo email per ricevere la newsletter, come descritto
              nell'<Link to="/privacy" className="font-semibold text-stone-700 underline decoration-gold decoration-2 underline-offset-2">informativa privacy</Link>.
              Posso revocare il consenso in ogni momento. Nessuna cessione a terzi, nessuna profilazione.
            </span>
          </label>
        </form>
      )}
      {status === "error" && <p className="text-rose-600 text-xs mt-3" data-testid="newsletter-error">{msg}</p>}
      </div>
    </div>
  );
}
